// Library Utilities
import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";

// Components
import ErrorMessage from "../../../../../Shared/error-message/errorMessage";
import InputField from "../../Shared/inputField/inputField";

// Styles
import Styles from "./passportServicePopup.module.scss";
import {
  buttonStyles,
  StyledButton,
} from "../../../../../Shared/button/button";
import dropDownStyles from "../../../../../Shared/dropDownStyles";

// constants
import {
  days,
  months,
  expiryYears,
  countryList,
  passportList,
} from "../../../../../constants/data";

// Functions
import {
  validName,
  validPassportNumber,
} from "../../../../../utils/validation";
import { getYears } from "../../../../../utils/getYears";
import { IdsGenerator } from "../../../../../utils/idGenerator";

function PassportServicePopup({ toastTimeout, handleResponse, name }: any) {
  const errorObject = {
    firstNameError: false,
    lastNameError: false,
    dobDayError: false,
    dobMonthError: false,
    dobYearError: false,
    genderError: false,
    expiryDayError: false,
    expiryMonthError: false,
    expiryYearError: false,
    passportNumberError: false,
    countryError: false,
    typeError: false,
    consentError: false,
  };

  const firstNameRef = useRef<any>("");
  const lastNameRef = useRef<any>("");
  const dateRef = useRef<any>(null);
  const monthRef = useRef<any>(null);
  const yearRef = useRef<any>(null);
  const expiryDateRef = useRef<any>(null);
  const expiryMonthRef = useRef<any>(null);
  const expiryYearRef = useRef<any>(null);
  const countryRef = useRef<any>(null);
  const passportNumberRef = useRef<any>("");
  const typeRef = useRef<any>(null);

  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [expiryDay, setExpiryDay] = useState("");
  const [expiryMonth, setExpiryMonth] = useState("");
  const [expiryYear, setExpiryYear] = useState("");
  const [countryName, setCountryName] = useState("");
  const [typeName, setTypeName] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [error, setError] = useState({ ...errorObject });
  const [isDisabled, setIsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedGender, setSelectedGender] = useState("");

  // checking first name valid or not on change
  function checkFirstName(forRunButton: boolean = false) {
    const fName = firstNameRef.current.value.toUpperCase();
    const isFirstNameValid = validName(fName) ? true : false;
    if (forRunButton) {
      return isFirstNameValid;
    } else {
      setError({ ...error, firstNameError: !isFirstNameValid });
    }
  }

  const yearOptions = getYears(1950);

  // checking last name valid or not on change
  function checkLastName(forRunButton: boolean = false) {
    const lName = lastNameRef.current.value.toUpperCase();
    const isLastNameValid = validName(lName) ? true : false;
    if (forRunButton) {
      return isLastNameValid;
    } else {
      setError({ ...error, lastNameError: !isLastNameValid });
    }
  }

  // checking passport number valid or not on change
  function checkPassportNumber(forRunButton: boolean = false) {
    const pNumber = passportNumberRef.current.value.toUpperCase();
    const isPassportNumberValid = validPassportNumber(pNumber) ? true : false;
    if (forRunButton) {
      return isPassportNumberValid;
    } else {
      setError({ ...error, passportNumberError: !isPassportNumberValid });
    }
  }

  // conditions to check Run verification button should be enabled or disabled
  useEffect(() => {
    try {
      if (
        !checkFirstName(true) ||
        !checkLastName(true) ||
        !checkPassportNumber(true) ||
        error.firstNameError ||
        error.lastNameError ||
        error.passportNumberError ||
        !day ||
        !month ||
        !year ||
        !expiryDay ||
        !expiryMonth ||
        !expiryYear ||
        error.dobDayError ||
        error.dobMonthError ||
        error.dobYearError ||
        error.expiryDayError ||
        error.expiryMonthError ||
        error.expiryYearError ||
        !selectedGender ||
        error.genderError ||
        !countryName ||
        error.countryError ||
        !typeName ||
        error.typeError
      ) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
    } catch (err) {
      setIsDisabled(true);
      toastTimeout(true, "error", (err as Error).message);
    }
  }, [error]);

  function handleCheck() {
    const firstName = firstNameRef.current.value.toUpperCase();
    const lastName = lastNameRef.current.value.toUpperCase();
    const date = `${day}/${month}/${year}`;
    const expiryDate = `${expiryDay}/${expiryMonth}/${expiryYear}`;
    const gender = !selectedGender ? "" : selectedGender === "Male" ? "M" : "F";
    const passportNumber = validPassportNumber(
      passportNumberRef.current.value.toUpperCase()
    )
      ? passportNumberRef.current.value.toUpperCase()
      : "NA";
    const consent = isChecked === true ? "Y" : "N";

    if (!checkFirstName(true)) {
      setError((error) => ({
        ...error,
        firstNameError: true,
      }));
      return false;
    } else if (!checkLastName(true)) {
      setError((error) => ({
        ...error,
        lastNameError: true,
      }));
      return false;
    } else if (!checkPassportNumber(true)) {
      setError((error) => ({
        ...error,
        passportNumberError: true,
      }));
      return false;
    } else if (!day) {
      setError((error) => ({
        ...error,
        dobDayError: true,
      }));
      return false;
    } else if (!month) {
      setError((error) => ({
        ...error,
        dobMonthError: true,
      }));
      return false;
    } else if (!year) {
      setError((error) => ({
        ...error,
        dobYearError: true,
      }));
      return false;
    } else if (!expiryDay) {
      setError((error) => ({
        ...error,
        expiryDayError: true,
      }));
      return false;
    } else if (!expiryMonth) {
      setError((error) => ({
        ...error,
        expiryMonthError: true,
      }));
      return false;
    } else if (!expiryYear) {
      setError((error) => ({
        ...error,
        expiryYearError: true,
      }));
      return false;
    } else if (!typeName) {
      setError((error) => ({
        ...error,
        typeError: true,
      }));
      return false;
    } else if (!countryName) {
      setError((error) => ({
        ...error,
        countryError: true,
      }));
      return false;
    } else if (!gender) {
      setError((error) => ({
        ...error,
        genderError: true,
      }));
      return false;
    } else if (!isChecked) {
      setError((error) => ({
        ...error,
        consentError: true,
      }));
      return false;
    }
    setLoading(true);
    handleResponse(
      {
        firstName,
        lastName,
        dob: date,
        expiryDate,
        country: countryName,
        gender,
        passportNumber,
        type: typeName,
        consent,
      },
      setLoading
    );
  }

  return (
    <div className={`${Styles.Content}`}>
      <div className={`${Styles.infoForm} pl-2`}>
        <hr />
        <p className="mt-2">Fill in the below details to run verification</p>
        <div className="d-flex flex-column">
          <div className="d-flex gap-3">
            <div className="d-flex flex-grow-1 flex-column justify-content-between">
              <div className={`${Styles.inputContent} d-flex flex-column`}>
                <label className={`${Styles.label}`}>First Name*</label>
                <InputField
                  id={IdsGenerator({
                    prefix: "passport service popup",
                    id: "change",
                    suffix: "first name",
                  })}
                  style={{ width: "100%" }}
                  onChange={checkFirstName}
                  inputRef={firstNameRef}
                  isError={error.firstNameError}
                  purpose="forPassport"
                />
                {error.firstNameError && (
                  <ErrorMessage>First Name field is Invalid</ErrorMessage>
                )}
              </div>
              <div className={`${Styles.inputContent} d-flex mt-2 flex-column`}>
                <label className={`${Styles.label}`}>DOB</label>
                <div className="d-flex mr-1">
                  <div className="mr-2 d-flex gap-3">
                    <div className="d-flex flex-column">
                      <Select
                        id={IdsGenerator({
                          prefix: "passport service popup",
                          id: "select",
                          suffix: "date",
                        })}
                        ref={dateRef}
                        onChange={(e: any) => {
                          setDay(e.value);
                          setError({ ...error, dobDayError: false });
                        }}
                        options={days}
                        styles={
                          !error.dobDayError
                            ? dropDownStyles.customStylesForDays
                            : dropDownStyles.customStylesForDaysError
                        }
                        placeholder="DD"
                      />
                    </div>
                    <div className="ml-2 d-flex flex-column">
                      <Select
                        id={IdsGenerator({
                          prefix: "passport service popup",
                          id: "select",
                          suffix: "month",
                        })}
                        onChange={(e: any) => {
                          setMonth(e.value);
                          setError({ ...error, dobMonthError: false });
                        }}
                        ref={monthRef}
                        options={months}
                        styles={
                          !error.dobMonthError
                            ? dropDownStyles.customStylesForMonths
                            : dropDownStyles.customStylesForMonthsError
                        }
                        placeholder="MMM"
                      />
                    </div>
                    <div className="ml-2 d-flex flex-column">
                      <Select
                        id={IdsGenerator({
                          prefix: "passport service popup",
                          id: "select",
                          suffix: "year",
                        })}
                        onChange={(e: any) => {
                          setYear(e.value);
                          setError({ ...error, dobYearError: false });
                        }}
                        ref={yearRef}
                        placeholder="YYYY"
                        options={yearOptions}
                        styles={
                          !error.dobYearError
                            ? dropDownStyles.customStylesForYears
                            : dropDownStyles.customStylesForYearsError
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={`${Styles.inputContent} d-flex mt-2 flex-column`}>
                <label className={`${Styles.label}`}>Passport Number*</label>
                <InputField
                  id={IdsGenerator({
                    prefix: "passport service popup",
                    id: "change",
                    suffix: "passport number",
                  })}
                  style={{ width: "100%" }}
                  onChange={checkPassportNumber}
                  inputRef={passportNumberRef}
                  isError={error.passportNumberError}
                  purpose="forPassport"
                />
                {error.passportNumberError && (
                  <ErrorMessage>
                    Please enter a valid passport number
                  </ErrorMessage>
                )}
              </div>
              <div className={`${Styles.inputContent} d-flex mt-2 flex-column`}>
                <label className={`${Styles.label}`}>Passport Type</label>
                <Select
                  id={IdsGenerator({
                    prefix: "passport service popup",
                    id: "select",
                    suffix: "passport type",
                  })}
                  onChange={(e: any) => {
                    setTypeName(e.value);
                    setError({ ...error, typeError: false });
                  }}
                  placeholder=""
                  ref={typeRef}
                  options={passportList}
                  styles={
                    !error.typeError
                      ? dropDownStyles.customStylesForCountry
                      : dropDownStyles.customStylesForCountryError
                  }
                />
                {error.typeError && (
                  <ErrorMessage>Please select type!</ErrorMessage>
                )}
              </div>
            </div>

            <div className="d-flex flex-column flex-grow-1 gap-3">
              <div className={`${Styles.inputContent} d-flex flex-column`}>
                <label className={`${Styles.label} ml-2`}>Last Name*</label>
                <div className="ml-2">
                  <InputField
                    id={IdsGenerator({
                      prefix: "passport service popup",
                      id: "change",
                      suffix: "last name",
                    })}
                    onChange={checkLastName}
                    style={{ width: "100%" }}
                    inputRef={lastNameRef}
                    isError={error.lastNameError}
                    purpose="forPassport"
                  />
                </div>
                {error.lastNameError && (
                  <ErrorMessage>Last Name field is Invalid</ErrorMessage>
                )}
              </div>
              <div className={`${Styles.inputContent} d-flex mt-2 flex-column`}>
                <label className={`${Styles.label} ml-2`}>Gender*</label>
                <div className={`${Styles.genderSelector} d-flex ml-2 gap-3`}>
                  <input
                    id={IdsGenerator({
                      prefix: "passport service popup",
                      id: "select female",
                      suffix: "gender",
                    })}
                data-testid={IdsGenerator({
                      prefix: "passport service popup",
                      id: "select female",
                      suffix: "gender",
                    })}
                    required
                    type="radio"
                    name="gender"
                    value="Female"
                    onChange={(e) => {
                      setSelectedGender(e.target.value);
                      setError((prevError) => ({
                        ...prevError,
                        genderError: false,
                      }));
                    }}
                  />
                  <label
                    className="mr-2"
                    htmlFor={IdsGenerator({
                      prefix: "passport service popup",
                      id: "select female",
                      suffix: "gender",
                    })}
                    style={{ width: "100%" }}
                  >
                    Female
                  </label>

                  <input
                    id={IdsGenerator({
                      prefix: "passport service popup",
                      id: "select male",
                      suffix: "gender",
                    })}
                data-testid={IdsGenerator({
                      prefix: "passport service popup",
                      id: "select male",
                      suffix: "gender",
                    })}
                    required
                    type="radio"
                    name="gender"
                    value="Male"
                    onChange={(e) => {
                      setSelectedGender(e.target.value);
                      setError((prevError) => ({
                        ...prevError,
                        genderError: false,
                      }));
                    }}
                  />
                  <label
                    htmlFor={IdsGenerator({
                      prefix: "passport service popup",
                      id: "select male",
                      suffix: "gender",
                    })}
                    style={{ width: "100%" }}
                  >
                    Male
                  </label>
                </div>
                {error.genderError && (
                  <ErrorMessage>Please select gender!</ErrorMessage>
                )}
              </div>
              <div className={`${Styles.inputContent} d-flex flex-column`}>
                <label className={`${Styles.label} ml-2`}>
                  Passport Expiry Date
                </label>
                <div className="d-flex mr-1 ml-2 gap-3">
                  <div className="mr-2 d-flex gap-3">
                    <div className="d-flex flex-column">
                      <Select
                        id={IdsGenerator({
                          prefix: "passport service popup",
                          id: "select expiry",
                          suffix: "date",
                        })}
                        onChange={(e: any) => {
                          setExpiryDay(e.value);
                          setError({ ...error, expiryDayError: false });
                        }}
                        ref={expiryDateRef}
                        options={days}
                        styles={
                          !error.expiryDayError
                            ? dropDownStyles.customStylesForDays
                            : dropDownStyles.customStylesForDaysError
                        }
                        placeholder="DD"
                      />
                    </div>
                    <div className="ml-2 d-flex flex-colmun">
                      <Select
                        id={IdsGenerator({
                          prefix: "passport service popup",
                          id: "select expiry",
                          suffix: "month",
                        })}
                        onChange={(e: any) => {
                          setExpiryMonth(e.value);
                          setError({ ...error, expiryMonthError: false });
                        }}
                        ref={expiryMonthRef}
                        options={months}
                        styles={
                          !error.expiryMonthError
                            ? dropDownStyles.customStylesForMonths
                            : dropDownStyles.customStylesForMonthsError
                        }
                        placeholder="MMM"
                      />
                    </div>
                    <div className="ml-2 d-flex flex-column">
                      <Select
                        id={IdsGenerator({
                          prefix: "passport service popup",
                          id: "select expiry",
                          suffix: "year",
                        })}
                        onChange={(e: any) => {
                          setExpiryYear(e.value);
                          setError({ ...error, expiryYearError: false });
                        }}
                        ref={expiryYearRef}
                        placeholder="YYYY"
                        options={expiryYears}
                        styles={
                          !error.expiryYearError
                            ? dropDownStyles.customStylesForYears
                            : dropDownStyles.customStylesForYearsError
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`${Styles.inputContent} d-flex mt-2 flex-column ml-2`}
              >
                <label className={`${Styles.label} ml-1`}>Country</label>
                <Select
                  id={IdsGenerator({
                    prefix: "passport service popup",
                    id: "select",
                    suffix: "country",
                  })}
                  onChange={(e: any) => {
                    setCountryName(e.value);
                    setError({ ...error, countryError: false });
                  }}
                  ref={countryRef}
                  options={countryList}
                  styles={
                    !error.countryError
                      ? dropDownStyles.customStylesForCountry
                      : dropDownStyles.customStylesForCountryError
                  }
                  placeholder=""
                />
              </div>
              {error.countryError && (
                <ErrorMessage>Please select country!</ErrorMessage>
              )}
            </div>
          </div>
          <div className="pt-4 pl-4 pb-4 d-flex gap-3">
            <input
              id={IdsGenerator({
                prefix: "passport service popup",
                id: "check",
                suffix: "consent",
              })}
                data-testid={IdsGenerator({
                prefix: "passport service popup",
                id: "check",
                suffix: "consent",
              })}
              required
              type="checkbox"
              //
              className={`${Styles.formCheckInput} `}
              onChange={(e: any) => setIsChecked(e.target.checked)}
              style={error.consentError ? { border: "1px solid red" } : {}}
            />
            <p className={`${Styles.ackText} mb-0`}>
              I hereby agree, to let zoop.one verify my data for verification
            </p>
            {error.consentError && (
              <ErrorMessage>Please provide consent!</ErrorMessage>
            )}
          </div>
          <div className={`${Styles.buttonGroup}`}>
            <StyledButton
              id={IdsGenerator({
                prefix: "passport service popup",
                id: "click",
                suffix: "run verification",
              })}
              hoverStyle={{ ...buttonStyles.primary.hover }}
              disabled={isDisabled}
              isLoading={loading}
              style={{ ...buttonStyles.primary.normal }}
              onClick={() => {
                setTimeout(() => setError({ ...errorObject }), 2000);
                handleCheck();
              }}
            >
              Run Verification
            </StyledButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PassportServicePopup;
